/*
 * Copyright (c) CIRROM 2025.
 */

import React, { useMemo } from "react";
import { CircularProgress, Box, CardHeader } from "@mui/material";
import { SummaryByFieldOverBatch } from "../../api/dashboards";
import { MiraCard, MiraCardContent } from "../mira/components/common/Card";
import { generateHslColor } from "../../theme/chartColors";
import styled from "@emotion/styled";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";

const ChartContainer = styled.div`
  height: 400px;
`;

interface SummaryByFieldChartProps {
  summaryByFieldOverBatches: SummaryByFieldOverBatch[];
  isLoading: boolean;
}

export const SummaryByFieldChart: React.FC<SummaryByFieldChartProps> = ({
  summaryByFieldOverBatches,
  isLoading,
}) => {
  const chartData = useMemo(() => {
    if (!summaryByFieldOverBatches || summaryByFieldOverBatches.length === 0) {
      return { labels: [], datasets: [] };
    }

    // Extract unique change_field values
    const fields = Array.from(
      new Set(summaryByFieldOverBatches.map((item) => item.change_field))
    );

    // Extract unique dates (date portion only), sorted chronologically
    const dates = Array.from(
      new Set(
        summaryByFieldOverBatches.map(
          (item) => item.last_update_time.split("T")[0]
        )
      )
    ).sort((a, b) => a.localeCompare(b));

    // Build a map: date -> field -> summed total
    const dataMap = new Map<string, Map<string, number>>();
    for (const date of dates) {
      dataMap.set(date, new Map());
    }

    for (const item of summaryByFieldOverBatches) {
      const date = item.last_update_time.split("T")[0];
      const fieldMap = dataMap.get(date)!;
      fieldMap.set(
        item.change_field,
        (fieldMap.get(item.change_field) || 0) + item.total
      );
    }

    // Format labels as "MMM dd, yyyy"
    const labels = dates.map((dateStr) => {
      const d = new Date(dateStr + "T00:00:00");
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      });
    });

    // Sort fields by grand total descending so highest-count fields sit at the stack base
    const fieldTotals = new Map<string, number>();
    for (const field of fields) {
      fieldTotals.set(
        field,
        dates.reduce(
          (sum, date) => sum + (dataMap.get(date)!.get(field) || 0),
          0
        )
      );
    }
    const sortedFields = [...fields].sort(
      (a, b) => fieldTotals.get(b)! - fieldTotals.get(a)!
    );

    // One dataset per field, using HSL hue rotation for distinct colors
    const datasets = sortedFields.map((field, index) => ({
      label: field,
      data: dates.map((date) => dataMap.get(date)!.get(field) || 0),
      backgroundColor: generateHslColor(index, fields.length, 55),
      borderColor: generateHslColor(index, fields.length, 45),
      borderWidth: 1,
      stack: "stack1",
    }));

    return { labels, datasets };
  }, [summaryByFieldOverBatches]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: "top" as const,
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          color: "rgba(0,0,0,0.0375)",
        },
      },
      y: {
        stacked: true,
        grid: {
          color: "rgba(0,0,0,0.0375)",
        },
        beginAtZero: true,
        title: {
          display: true,
          text: "Total Changes",
        },
      },
    },
  };

  return (
    <>
      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CircularProgress />
        </Box>
      ) : (
        <MiraCard>
          <CardHeader title="Changes by Field Over Time" />
          <MiraCardContent>
            <ChartContainer>
              <Bar data={chartData} options={chartOptions} />
            </ChartContainer>
          </MiraCardContent>
        </MiraCard>
      )}
    </>
  );
};
